import { OrderFulfillmentType, OrderType } from "~/entities/orderEntities";
import type { ServiceAvailability } from "~/entities/siteEntities";

/**
 * Decide which of Pickup / Delivery the StoreHeader should offer (vs grey out)
 * for the current experience at this site.
 *
 * Rules:
 *  - Only the CURRENT order source's order types count (online delivery must not
 *    un-grey delivery inside catering).
 *  - A fulfilment is offered when it has an active order type that is ALSO
 *    currently available (present in `serviceAvailability` with `available`).
 *  - The currently-selected fulfilment is ALWAYS available, so it never greys
 *    itself.
 *  - Safe-degrade: when `serviceAvailability` is absent/empty (older backend,
 *    unprovisioned org), fall back to active-order-type existence so a genuinely
 *    available fulfilment is never wrongly greyed.
 */
export const fulfilmentAvailability = (
  orderTypes: OrderType[],
  serviceAvailability: ServiceAvailability[] | undefined,
  selected: OrderType | undefined,
): { pickupAvailable: boolean; deliveryAvailable: boolean } => {
  const sa = serviceAvailability ?? [];
  const hasAvailabilityData = sa.length > 0;
  const availableCodes = new Set(
    sa.filter((s) => s.available).map((s) => s.orderTypeCode),
  );
  const sourceOrderTypes = orderTypes.filter(
    (t) =>
      t.active &&
      t.source === selected?.source &&
      (!hasAvailabilityData || availableCodes.has(t.code)),
  );
  return {
    pickupAvailable:
      selected?.fulfillmentType === OrderFulfillmentType.COLLECTION ||
      sourceOrderTypes.some(
        (t) => t.fulfillmentType === OrderFulfillmentType.COLLECTION,
      ),
    deliveryAvailable:
      selected?.fulfillmentType === OrderFulfillmentType.DELIVERY ||
      sourceOrderTypes.some(
        (t) => t.fulfillmentType === OrderFulfillmentType.DELIVERY,
      ),
  };
};
